import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Paper, Typography, Box, useTheme, CircularProgress } from "@mui/material";
import { Link } from 'react-router-dom';
import { useGetSampleADCOrganizationsQuery } from '../../../../features/organizations/organizationsApiSlice';
import { useDiscreteMode } from '../DiscreteMode';
import { tokens } from '../../../../theme';
import { motion } from 'framer-motion';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-tooltip">
                <p className="label">{`Date: ${label}`}</p>
                {payload.map((entry, index) => (
                    <p key={index} style={{ color: entry.stroke }}>{`${entry.name}: ${(entry.value * 100).toFixed(2)}%`}</p>
                ))}
            </div>
        );
    }
    return null;
};

const ADCTrafficChart = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { data: organizations, isLoading, isError } = useGetSampleADCOrganizationsQuery();

    console.log("Organizations:", organizations);

    const orgArray = organizations || [];

    const [dateRange, setDateRange] = useState('7d');
    const [selectedChartOrgs, setSelectedChartOrgs] = useState([]);
    const [selectedStat, setSelectedStat] = useState('resolution_rate');

    const { isDiscreteMode } = useDiscreteMode();

    const renderOrganizationName = (name) => isDiscreteMode ? 'Confidential' : name;

    const [orgColors, setOrgColors] = useState({});

    const organizationsData = useMemo(() => {
        if (!organizations) return [];
        return orgArray.flatMap(organization => {
            return organization.adc_counts
                .filter(count => count.on_date !== 'summary')
                .map((count, index) => ({
                    id: `${organization._id}-${index}`,
                    ...count,
                    member_name: organization.name,
                    resolution_rate: count.visits_resolved_count / count.visits_count,
                }));
        });
    }, [organizations]);

    useEffect(() => {
        console.log("Organizations Data:", organizationsData);
    }, [organizationsData]);

    const chartData = useMemo(() => {
        if (!organizationsData || selectedChartOrgs.length === 0) return [];
        
        const today = new Date();
        const startDate = dateRange === '7d' ? new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)
            : dateRange === '30d' ? new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30)
                : new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
        
        const dataByDate = {};
        for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
            const formattedDate = d.toISOString().split('T')[0];
            dataByDate[formattedDate] = {};
            selectedChartOrgs.forEach(org => {
                dataByDate[formattedDate][org] = null;
            });
        }
        
        organizationsData.forEach(item => {
            const itemDate = item.on_date;
            if (itemDate in dataByDate && selectedChartOrgs.includes(item.member_name)) {
                const statValue = (selectedStat in item && typeof item[selectedStat] === 'number') ? item[selectedStat] : 0;
                dataByDate[itemDate][item.member_name] = statValue;
            }
        });
        
        const chartData = Object.keys(dataByDate).map(date => ({
            date,
            ...dataByDate[date]
        }));

        console.log("Chart Data:", chartData);

        return chartData;
    }, [organizationsData, selectedChartOrgs, selectedStat, dateRange]);

    const getRandomColor = () => {
        const getLowValue = () => Math.floor(Math.random() * 178) + 77; // 77 to 255 range for each color channel
        const red = getLowValue();
        const green = getLowValue();
        const blue = getLowValue();
        return `rgb(${red}, ${green}, ${blue})`;
    };

    const prevOrgsRef = useRef();

    useEffect(() => {
        const hasOrgsChanged = JSON.stringify(orgArray) !== JSON.stringify(prevOrgsRef.current);
        if (hasOrgsChanged) {
            prevOrgsRef.current = orgArray;
            const newOrgColors = orgArray.reduce((acc, org) => {
                acc[org.name] = getRandomColor();
                return acc;
            }, {});
            setOrgColors(newOrgColors);
        }
    }, [orgArray]);

    useEffect(() => {
        if (!isLoading && orgArray.length > 0) {
            const eligibleOrgs = orgArray.filter(org =>
                org.adc_counts && org.adc_counts.some(count =>
                    count.visits_resolved_count / count.visits_count > 0.3 // filter organizations with resolution rate over 30%
                )
            ).map(org => org.name);

            const randomOrgs = [];
            while (randomOrgs.length < 5 && eligibleOrgs.length > randomOrgs.length) {
                const randomOrg = eligibleOrgs[Math.floor(Math.random() * eligibleOrgs.length)];
                if (!randomOrgs.includes(randomOrg)) {
                    randomOrgs.push(randomOrg);
                }
            }
            setSelectedChartOrgs(randomOrgs);
        }
    }, [orgArray, selectedStat, isLoading]);

    useEffect(() => {
        console.log("Selected Chart Orgs:", selectedChartOrgs);
    }, [selectedChartOrgs]);

    if (isLoading) {
        return (
            <Paper sx={{ p: 4, minHeight: '33%', borderRadius: '20px', backgroundColor: colors.grey[700] }}>
                <Typography variant="h4" gutterBottom sx={{ alignSelf: 'flex-start' }}>
                    <Link to="/console/media-adc" style={{ color: colors.blueAccent[200], textDecoration: 'none' }}>ADC Counts</Link>
                </Typography>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                        <CircularProgress color="primary" />
                    </Box>
                </motion.div>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 4, minHeight: '33%', borderRadius: '20px', backgroundColor: colors.grey[700] }}>
            <Typography variant="h4" gutterBottom sx={{ alignSelf: 'flex-start' }}>
                <Link to="/console/media-adc" style={{ color: colors.blueAccent[200], textDecoration: 'none' }}>ADC Counts</Link>
            </Typography>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <Box sx={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart width={375} height={300} data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontWeight: 'bold', fontSize: 12, fill: colors.blueAccent[100] }} />
                            <YAxis 
                                tickFormatter={(tick) => `${(tick * 100).toFixed(0)}%`} 
                                tick={{ fontWeight: 'bold', fontSize: 12, fill: colors.blueAccent[100] }}
                                label={{ 
                                    value: 'Resolution Rate (%)', 
                                    angle: -90, 
                                    position: 'insideLeft', 
                                    style: { textAnchor: 'middle', fill: colors.blueAccent[100], fontSize: 14, fontWeight: 'bold' }
                                }} 
                            />
                            <Legend wrapperStyle={{ color: colors.blueAccent[100], fontWeight: 'bold' }} />
                            <Tooltip content={<CustomTooltip />} />
                            {selectedChartOrgs.map(org => (
                                <Line
                                    key={org}
                                    type="monotone"
                                    dataKey={org}
                                    name={renderOrganizationName(org)}
                                    stroke={orgColors[org]}
                                    strokeWidth={3} // Make the line thicker
                                    activeDot={{ r: 8 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </motion.div>
        </Paper>
    );
};

export default ADCTrafficChart;
